import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { WelcomeTour, tourSeen } from '@/app/tour/WelcomeTour';
import { useAuth } from '@/auth/AuthProvider';
import { helpFor, type HelpTopic } from './helpContent';
import { HelpSheet } from './HelpSheet';

interface HelpContextValue {
  /** Help for the current screen, if there is any. */
  topic: HelpTopic | null;
  openHelp: () => void;
  startTour: () => void;
}

const HelpContext = createContext<HelpContextValue>({ topic: null, openHelp: () => {}, startTour: () => {} });

export function useHelp() {
  return useContext(HelpContext);
}

/** The help panel and the welcome tour, available on every screen. The tour starts by itself once per person. */
export function HelpProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { me } = useAuth();
  const userId = me?.user.id;
  const [helpOpen, setHelpOpen] = useState(false);
  const [tourRequested, setTourRequested] = useState(false);
  const [tourDismissed, setTourDismissed] = useState(false);
  const topic = helpFor(pathname);

  const startTour = useCallback(() => {
    setHelpOpen(false);
    setTourRequested(true);
  }, []);
  const value = useMemo(() => ({ topic, openHelp: () => setHelpOpen(true), startTour }), [topic, startTour]);

  // First visit to the home screen: the tour shows by itself, once per person.
  const autoTour = !!userId && pathname === '/inicio' && !tourDismissed && !tourSeen(userId);
  const tourOpen = tourRequested || autoTour;

  return (
    <HelpContext.Provider value={value}>
      {children}
      <HelpSheet open={helpOpen} onOpenChange={setHelpOpen} topic={topic} onTour={startTour} />
      {tourOpen && userId && (
        <WelcomeTour
          userId={userId}
          onClose={() => {
            setTourRequested(false);
            setTourDismissed(true);
          }}
        />
      )}
    </HelpContext.Provider>
  );
}
